import React, { useState, useEffect } from "react";
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    message,
    Spin,
    Select,
} from "antd";
import {
    PlusOutlined,
    SearchOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "../json/appConstant.json";

const PropertyCommercialSubCategories = ({ isDarkMode }) => {
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const [propertyCategories, setPropertyCategories] = useState([]); // new state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isDataLoading, setIsDataLoading] = useState(false);
    const { jsonApi } = useApis();

    useEffect(() => {
        fetchCategories(pagination.current, pagination.pageSize, search);
        fetchPropertyCategories(); // Fetch property categories
    }, [pagination.current, pagination.pageSize, search]);

    const fetchCategories = async (page, pageSize, search = "") => {
        setIsDataLoading(true);
        const params = {
            page,
            pageSize,
            search,
        };

        const response = await jsonApi(appConstant.CommercialSubCategory, params, {}, null);

        if (response?.data?.code === 200 && response?.data?.status === "success") {
            const data = response.data.data.categories || response.data.data;
            setCategories(
                data.map((cat) => ({
                    key: cat.Id,
                    id: cat.Id,
                    CommCategorySubName: cat.CommCategorySubName,
                    CommID: cat.M_CommercialCategory.CommCategoryName,
                    isActive: cat.IsActive,
                }))
            );
            setPagination((prev) => ({
                ...prev,
                total: response.data.data.total || data.length,
            }));
        }
        setIsDataLoading(false);
    };

    const fetchPropertyCategories = async () => {
        try {
            const response = await jsonApi(appConstant.CommercialCategory, null, {}, null);
            if (response?.data?.code === 200 && response?.data?.status === "success") {
                const data = response.data.data.categories || response.data.data;
                setPropertyCategories(data);
            }
        } catch (error) {
            console.error("Error fetching commercial categories:", error);
            message.error("Failed to load commercial categories");
        }
    };

    const handleAddCategory = async (values) => {
        setLoading(true);
        try {
            const payload = {
                CommCategorySubName: values.CommCategorySubName,
                // Charges: Number(values.charges),
                CommID: values.CommID,
                IsActive: true,
            };

            const response = await jsonApi(
                appConstant.createCommercialCategory,
                null,
                payload,
                null
            );

            if (response.data.code === 200) {
                message.success("Commercial Sub category added successfully");
                fetchCategories(pagination.current, pagination.pageSize);
                setIsModalVisible(false);
                form.resetFields();
            } else {
                message.error(response.message || "Failed to add category");
            }
        } catch (error) {
            console.error("Add Commercial Category Error:", error);
            message.error("An error occurred");
        }
        setLoading(false);
    };

    const columns = [
        {
            title: "S.No",
            key: "serialNumber",
            render: (_, __, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Commercial Sub Category Name",
            dataIndex: "CommCategorySubName",
            key: "CommCategorySubName",
            sorter: (a, b) => a.CommCategorySubName.localeCompare(b.CommCategorySubName),
        },
        {
            title: "Commercial Category",
            dataIndex: "CommID",
            key: "CommID",

        },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            render: (active) => (active ? "Yes" : "No"),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                }}
            >
                <h2 style={{ color: isDarkMode ? "#fff" : "#000" }}>
                    Commercial Sub Categories
                </h2>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalVisible(true)}
                >
                    Add Commercial Sub Category
                </Button>
            </div>

            <Input
                placeholder="Search commercial sub categories..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                suffix={
                    search && (
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setSearch("");
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        />
                    )
                }
                style={{ width: 300, marginBottom: 16 }}
            />

            <Card
                style={{
                    background: isDarkMode ? "#1f1f1f" : "#fff",
                }}
            >
                <Spin spinning={isDataLoading}>
                    <Table
                        columns={columns}
                        dataSource={categories}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                            onChange: (page, pageSize) => {
                                setPagination({ current: page, pageSize });
                            },
                        }}
                        rowKey="id"
                    />
                </Spin>
            </Card>

            <Modal
                visible={isModalVisible}
                title="Add Commercial Category"
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setIsModalVisible(false);
                            form.resetFields();
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={() => form.submit()}
                    >
                        Add
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical" onFinish={handleAddCategory}>
                    <Form.Item
                        name="CommID"
                        label="Commercial Category"
                        rules={[{ required: true, message: "Please select a commercial category" }]}
                    >
                        <Select placeholder="Select commercial category">
                            {propertyCategories.map((category) => (
                                <Select.Option key={category.id} value={category.id}>
                                    {category.CommCategoryName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Commercial Sub Category Name"
                        rules={[
                            { required: true, message: "Please enter a category name" },
                            { max: 250, message: "Maximum 250 characters allowed" },
                            {
                                pattern: /^[A-Za-z\s]+$/,
                                message: "Only alphabets and spaces are allowed",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Enter category name" rows={4} />
                    </Form.Item>


                </Form>
            </Modal>
        </div>
    );
};

export default PropertyCommercialSubCategories;
